import React, { useState, useRef, useEffect } from "react";
import frame from "../../Assets/img/upload-image.png";
import axios from "axios";
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { useQuantityContext } from "../QuantityContext";

const ActiveStep1 = ({ setFormData, setActiveStep, step1Data, updateStep1Data, isSignup }) => {
  const [selectedImage, setSelectedImage] = useState(step1Data.selectedImage || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [firstName, setFirstName] = useState(step1Data.firstName || "");
  const [lastName, setLastName] = useState(step1Data.lastName || "");
  const [dob, setDob] = useState(step1Data.dob || "");
  const [timeOfBirth, setTimeOfBirth] = useState(step1Data.timeOfBirth || "");
  const [dobDate, setDobDate] = useState(
    step1Data.dob ? dayjs(step1Data.dob) : null
  );
  const [dobTime, setDobTime] = useState(
    step1Data.timeOfBirth ? dayjs(step1Data.timeOfBirth, 'HH:mm:ss') : null
  );
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [dobError, setDobError] = useState(false);
  const [timeError, setTimeError] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const [micActive, setMicActive] = useState(false);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const dobRef = useRef(null);
  const cropperRef = useRef(null);
  const [cropperInitialized, setCropperInitialized] = useState(false);
  const { handleFailure, handleInfo, } = useQuantityContext();

  const [profilePicUrl, setProfilePicUrl] = useState('');

  // Function to handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setSelectedImage(imageUrl);
    setOpenDialog(true);
  };

  // Function to handle cropping and saving the image
  const handleCropSave = () => {
    console.log("Clicking save button");

    if (!cropperRef.current || !cropperRef.current.getCroppedCanvas) {
      console.error("Cropper not initialized or getCroppedCanvas function not available.");
      return;
    }

    const canvas = cropperRef.current.getCroppedCanvas();

    if (canvas) {
      canvas.toBlob(
        (blob) => {
          const croppedFile = new File([blob], selectedFile.name || 'croppedImage.jpg', {
            type: selectedFile.type || 'image/jpeg',
          });

          // Update selected image state
          setSelectedImage(URL.createObjectURL(croppedFile));

          // Prepare image object for form data
          const imageObject = {
            name: croppedFile.name,
            type: croppedFile.type,
            data: URL.createObjectURL(croppedFile),
          };

          // Update form data with cropped image object
          setFormData((prevFormData) => ({
            ...prevFormData,
            image: imageObject,
          }));
          setProfilePicUrl(URL.createObjectURL(croppedFile));
          // Close dialog after cropping
          setOpenDialog(false);

          // Upload the cropped file
          // handleFileUpload(croppedFile);
        },
        'image/jpeg'
      );
    } else {
      console.error("Canvas not available after cropping.");
    }
  };

  // Function to handle file upload to server
  const handleFileUpload = async (file) => {
    if (!file) {
      console.error('No file to upload.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/uploadpublicblob/generate-sas-url`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Assuming your response structure is like { sasUrl: 'some_url' }
      const sasUrl = response.data.url;

      setProfilePicUrl(sasUrl);

      console.log("File uploaded successfully!", sasUrl);

      setFormData((prevFormData) => ({
        ...prevFormData,
        image: sasUrl,
      }));

      // Proceed to next step or perform necessary actions after upload
      // setActiveStep((prevActiveStep) => prevActiveStep + 1);

    } catch (error) {
      console.error('Error uploading file:', error.message);
      handleFailure('Error uploading file. Please check the console for details.');
    }
  };

  const handleCropClose = () => {
    setOpenDialog(false);
    setSelectedImage(null);
    setSelectedFile(null);
  }

  // Function to handle microphone input for first and last name fields
  const handleMicClick = (fieldRef, setMicBlink) => {
    setMicActive(true);
    fieldRef.current.focus();
    setMicBlink(true);

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = ["en-US"];

    const onFocusChange = () => {
      if (document.activeElement !== fieldRef.current) {
        recognition.stop();
        setMicActive(false);
        setMicBlink(false);
      }
    };

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0])
        .map((result) => result.transcript)
        .join("");

      if (document.activeElement) {
        const activeElementId = document.activeElement.id;
        if (activeElementId === "firstName") {
          setFirstName(transcript);
        } else if (activeElementId === "lastName") {
          setLastName(transcript);
        }
      }
    };

    recognition.onend = () => {
      setMicBlink(false);
    };

    fieldRef.current.addEventListener("blur", onFocusChange);

    recognition.start();
  };

  // Function to handle changes in first name input
  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
    setFirstNameError(false);
  };

  // Handle Date change
  const handleDateChange = (newValue) => {
    setDobDate(newValue);
    if (newValue) {
      // Format: YYYY-MM-DD
      const formattedDate = newValue.format('YYYY-MM-DD');
      setDob(formattedDate);
      setDobError(false);
    } else {
      setDob('');
    }
  };

  // Handle Time change
  const handleTimeChange = (newValue) => {
    setDobTime(newValue);
    if (newValue) {
      // Format: HH:mm:ss (24-hour format for payload)
      const formattedTime = newValue.format('HH:mm:ss');
      setTimeOfBirth(formattedTime);
      setTimeError(false);
    } else {
      setTimeOfBirth('');
    }
  };

  // Function to handle changes in last name input
  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
    setLastNameError(false);
  };

  // Function to handle submission of user's name
  const handleNameofUser = () => {
    if (firstName.trim() === "") {
      setFirstNameError(true);
    }

    if (lastName.trim() === "") {
      setLastNameError(true);
    }
    if (dob.trim() === "" || !dobDate) {
      setDobError(true);
    }
    if (timeOfBirth.trim() === "" || !dobTime) {
      setTimeError(true);
    }

    // Update form data and step data
    setFormData((prevFormData) => ({
      ...prevFormData,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dob: dob.trim(),
      timeOfBirth: timeOfBirth.trim(),
      // Full datetime for compatibility (if needed)
      // dateOfBirth: dob.trim() + ' ' + timeOfBirth.trim(),
    }));

    updateStep1Data({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dob: dob.trim(),
      timeOfBirth: timeOfBirth.trim(),
      selectedImage: selectedImage,
    });

    if (isSignup) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);

    }
    else {
      // Proceed to next step if all fields are filled
      if (firstName.trim() !== "" && lastName.trim() !== "" && dob.trim() !== "" && dobDate && timeOfBirth.trim() !== "" && dobTime) {
        if (typeof setActiveStep === 'function') {
          setActiveStep(); // This will scroll to next section
        }
      }

    }
  };

  // Effect to initialize cropper once selected image changes
  useEffect(() => {
    if (openDialog && selectedImage && !cropperInitialized && cropperRef.current) {
      // console.log("Initializing cropper...");
      setCropperInitialized(true);
    }
  }, [openDialog, selectedImage, cropperInitialized]);

  // Function to handle cropper initialization
  const handleCropperInitialized = (instance) => {
    cropperRef.current = instance;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div style={{ width: '100%', maxWidth: '100%' }}>
        {/* Description text - FULLY VISIBLE */}
        {isSignup ? <div>
          <h2 className="onboard-slide-title">Hi, Welcome to Neoin!</h2>
          <p className="paragraph-text">
            Hey there! Want to make your profile stand out? Adding a profile picture
            and your full name is a great way to do it!
          </p>
        </div>
          :
          <div>
            <p className="paragraph-text" style={{
              textAlign: 'left',
              color: '#1a1a1a',
              fontSize: '16px',
              lineHeight: '1.6',
              marginBottom: '24px',
              fontWeight: 400,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              overflow: 'visible',
              maxHeight: 'none',
              height: 'auto',
              display: 'block'
            }}>
              Hey there! Want to make your profile stand out?<br />
              Adding a profile picture and your full name is a great choice.
            </p>
          </div>}
        {/* Avatar upload section - centered */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className={profilePicUrl ? "avatar-container" : "avatar-placeholder"}>
            {profilePicUrl ? (
              <img src={profilePicUrl} alt="Uploaded" className="uploaded-image" />
            ) : (
              <img
                src={frame}
                alt="Upload"
                style={{ cursor: 'pointer', maxWidth: '100%' }}
                onClick={() => document.getElementById("fileInput").click()}
              />
            )}
          </div>
          <input
            type="file"
            id="fileInput"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <div className="upload-button-container my-3">
            <Button
              variant="outlined"
              className="next-btn upload-button primary-outlined"
              onClick={() => document.getElementById("fileInput").click()}
              sx={{
                textTransform: 'uppercase',
                fontWeight: 600,
                color: '#0044C6',
                borderColor: '#0044C6',
                padding: '8px 32px',
                '&:hover': {
                  borderColor: '#0044C6',
                  backgroundColor: 'rgba(0,68,198,0.04)'
                }
              }}
            >
              BROWSE
            </Button>
          </div>
        </div>

        {/* Form fields - left aligned */}
        <div className='login-fields-container' style={{ textAlign: 'left', width: '100%' }}>
          <div className="login-fields" style={{ width: '100%' }}>
            <div className="a-inactive-enabled">
              <div className="overlap-group-wrapper">
                <div className="overlap-group-2">
                  <div className={`text-field-container mb-3 ${firstNameError ? "error" : ""}`}>
                    <TextField
                      inputRef={firstNameRef}
                      className="text-field-outline input-text mb-2"
                      id="firstName"
                      name="firstName"
                      label="First Name *"
                      variant="outlined"
                      required
                      error={firstNameError}
                      value={firstName}
                      onChange={handleFirstNameChange}
                      enabled={micActive}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#ffffff',
                          '& fieldset': {
                            borderColor: '#d0d5dd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#0044C6',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#0044C6',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#344054',
                          '&.Mui-focused': {
                            color: '#0044C6',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: '#1a1a1a',
                          padding: '16.5px 14px',
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#d32f2f',
                        },
                        '& .Mui-error': {
                          '& .MuiOutlinedInput-root fieldset': {
                            borderColor: '#d32f2f',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="a-inactive-enabled-wrapper">
              <div className="overlap-group-wrapper">
                <div className="overlap-group-2">
                  <div
                    className={`text-field-container ${lastNameError ? "error" : ""}`}
                  >
                    <TextField
                      inputRef={lastNameRef}
                      className="text-field-outline input-text mb-2"
                      id="lastName"
                      name="lastName"
                      label="Last Name *"
                      variant="outlined"
                      required
                      error={lastNameError}
                      value={lastName}
                      onChange={handleLastNameChange}
                      enabled={micActive}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#ffffff',
                          '& fieldset': {
                            borderColor: '#d0d5dd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#0044C6',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#0044C6',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#344054',
                          '&.Mui-focused': {
                            color: '#0044C6',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: '#1a1a1a',
                          padding: '16.5px 14px',
                        },
                        '& .Mui-error': {
                          '& .MuiOutlinedInput-root fieldset': {
                            borderColor: '#d32f2f',
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Date of Birth Picker - Only past dates allowed */}
            <div className="a-inactive-enabled-wrapper">
              <div className="overlap-group-wrapper">
                <div className="overlap-group-2">
                  <div
                    className={`text-field-container mb-3 ${dobError ? "error" : ""}`}
                  >
                    <DatePicker
                      label="Date of Birth *"
                      value={dobDate}
                      onChange={handleDateChange}
                      format="YYYY-MM-DD"
                      maxDate={dayjs()} // This prevents future date selection
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: dobError,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: '#ffffff',
                              '& fieldset': {
                                borderColor: '#d0d5dd',
                              },
                              '&:hover fieldset': {
                                borderColor: '#0044C6',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#0044C6',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: '#344054',
                              '&.Mui-focused': {
                                color: '#0044C6',
                              },
                            },
                            '& .MuiInputBase-input': {
                              color: '#1a1a1a',
                              padding: '16.5px 14px',
                            },
                            '& .Mui-error': {
                              '& .MuiOutlinedInput-root fieldset': {
                                borderColor: '#d32f2f',
                              },
                            },
                          }
                        }
                      }}
                    />
                    {dobError && (
                      <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}>
                        Date of Birth is required
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Time of Birth Picker - 12-hour format display, 24-hour format payload */}
            <div className="a-inactive-enabled-wrapper">
              <div className="overlap-group-wrapper">
                <div className="overlap-group-2">
                  <div
                    className={`text-field-container ${timeError ? "error" : ""}`}
                  >
                    <TimePicker
                      label="Time of Birth *"
                      value={dobTime}
                      onChange={handleTimeChange}
                      format="hh:mm:ss A" // 12-hour format with AM/PM
                      ampm={true} // Enable AM/PM
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: timeError,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: '#ffffff',
                              '& fieldset': {
                                borderColor: '#d0d5dd',
                              },
                              '&:hover fieldset': {
                                borderColor: '#0044C6',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#0044C6',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: '#344054',
                              '&.Mui-focused': {
                                color: '#0044C6',
                              },
                            },
                            '& .MuiInputBase-input': {
                              color: '#1a1a1a',
                              padding: '16.5px 14px',
                            },
                            '& .Mui-error': {
                              '& .MuiOutlinedInput-root fieldset': {
                                borderColor: '#d32f2f',
                              },
                            },
                          }
                        }
                      }}
                    />
                    {timeError && (
                      <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}>
                        Time of Birth is required
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Next button - centered */}
          <div className="next-button-container-1 pb-3" style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
            <Button
              variant="contained"
              className={`next-btn primary-contained`}
              onClick={handleNameofUser}
              disabled={firstName.trim() === "" || lastName.trim() === "" || dob.trim() === "" || !dobDate || timeOfBirth.trim() === "" || !dobTime}
              sx={{
                textTransform: 'uppercase',
                fontWeight: 600,
                backgroundColor: '#0044C6',
                color: '#ffffff',
                padding: '10px 40px',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#003399',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#999999'
                }
              }}
            >
              NEXT
            </Button>
          </div>
        </div>

        <Dialog open={openDialog} onClose={handleCropClose}>
          <DialogTitle>Crop Image</DialogTitle>
          <DialogContent>
            <div style={{ width: '100%', height: '400px', position: 'relative' }}>
              <Cropper
                ref={cropperRef}
                src={selectedImage}
                style={{ height: '100%', width: '100%' }}
                initialAspectRatio={1}
                guides={true}
                cropBoxMovable={true}
                cropBoxResizable={true}
                crossOrigin="anonymous"
                dragMode="crop"
                viewMode={1}
                background={false}
                responsive={true}
                autoCrop={false}
                autoCropArea={1}
                minCropBoxHeight={20}
                minCropBoxWidth={20}
                checkOrientation={false}
                onInitialized={handleCropperInitialized}
              />
            </div>
          </DialogContent>
          <DialogContent>
            <div className="d-flex justify-content-end mt-3 gap-3">
              <Button onClick={handleCropClose} className="hover-red-bg">Cancel</Button>
              <Button onClick={handleCropSave} variant="contained" sx={{ backgroundColor: '#0044C6' }}>Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </LocalizationProvider>
  );
};

export default ActiveStep1;


// import React, { useState, useRef, useEffect } from "react";
// import frame from "../../Assets/img/upload-image.png";
// import axios from "axios";
// import {
//   TextField,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Button,
// } from "@mui/material";
// import Cropper from "react-cropper";
// import "cropperjs/dist/cropper.css";
// import { useQuantityContext } from "../QuantityContext";

// const ActiveStep1 = ({ setFormData, setActiveStep, step1Data, updateStep1Data }) => {
//   const [selectedImage, setSelectedImage] = useState(step1Data.selectedImage || null);
//   const [selectedFile, setSelectedFile] = useState(null);
//   const [firstName, setFirstName] = useState(step1Data.firstName || "");
//   const [lastName, setLastName] = useState(step1Data.lastName || "");
//   const [dob, setDob] = useState(step1Data.dob || "");
//   const [firstNameError, setFirstNameError] = useState(false);
//   const [lastNameError, setLastNameError] = useState(false);
//   const [dobError, setDobError] = useState(false);
//   const [openDialog, setOpenDialog] = useState(false);
//   const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
//   const [micActive, setMicActive] = useState(false);
//   const firstNameRef = useRef(null);
//   const lastNameRef = useRef(null);
//   const dobRef = useRef(null);
//   const cropperRef = useRef(null);
//   const [cropperInitialized, setCropperInitialized] = useState(false);
//   const { handleFailure, handleInfo, } = useQuantityContext();

//   const [profilePicUrl, setProfilePicUrl] = useState('');

//   // Function to handle file selection
//   const handleFileSelect = (event) => {
//     const file = event.target.files[0];
//     if (!file) return;

//     const imageUrl = URL.createObjectURL(file);
//     setSelectedFile(file);
//     setSelectedImage(imageUrl);
//     setOpenDialog(true);
//   };

//   // Function to handle cropping and saving the image
//   const handleCropSave = () => {
//     console.log("Clicking save button");

//     if (!cropperRef.current || !cropperRef.current.getCroppedCanvas) {
//       console.error("Cropper not initialized or getCroppedCanvas function not available.");
//       return;
//     }

//     const canvas = cropperRef.current.getCroppedCanvas();

//     if (canvas) {
//       canvas.toBlob(
//         (blob) => {
//           const croppedFile = new File([blob], selectedFile.name || 'croppedImage.jpg', {
//             type: selectedFile.type || 'image/jpeg',
//           });

//           // Update selected image state
//           setSelectedImage(URL.createObjectURL(croppedFile));

//           // Prepare image object for form data
//           const imageObject = {
//             name: croppedFile.name,
//             type: croppedFile.type,
//             data: URL.createObjectURL(croppedFile),
//           };

//           // Update form data with cropped image object
//           setFormData((prevFormData) => ({
//             ...prevFormData,
//             image: imageObject,
//           }));
//           setProfilePicUrl(URL.createObjectURL(croppedFile));
//           // Close dialog after cropping
//           setOpenDialog(false);

//           // Upload the cropped file
//           // handleFileUpload(croppedFile);
//         },
//         'image/jpeg'
//       );
//     } else {
//       console.error("Canvas not available after cropping.");
//     }
//   };

//   // Function to handle file upload to server
//   const handleFileUpload = async (file) => {
//     if (!file) {
//       console.error('No file to upload.');
//       return;
//     }

//     try {
//       const formData = new FormData();
//       formData.append('file', file);

//       const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/uploadpublicblob/generate-sas-url`, formData, {
//         headers: {
//           'Content-Type': 'multipart/form-data',
//         },
//       });

//       // Assuming your response structure is like { sasUrl: 'some_url' }
//       const sasUrl = response.data.url;

//       setProfilePicUrl(sasUrl);

//       console.log("File uploaded successfully!", sasUrl);

//       setFormData((prevFormData) => ({
//         ...prevFormData,
//         image: sasUrl,
//       }));

//       // Proceed to next step or perform necessary actions after upload
//       // setActiveStep((prevActiveStep) => prevActiveStep + 1);

//     } catch (error) {
//       console.error('Error uploading file:', error.message);
//       handleFailure('Error uploading file. Please check the console for details.');
//     }
//   };

//   const handleCropClose = () => {
//     setOpenDialog(false);
//     setSelectedImage(null);
//     setSelectedFile(null);
//   }

//   // Function to handle microphone input for first and last name fields
//   const handleMicClick = (fieldRef, setMicBlink) => {
//     setMicActive(true);
//     fieldRef.current.focus();
//     setMicBlink(true);

//     const recognition = new SpeechRecognition();
//     recognition.continuous = true;
//     recognition.interimResults = true;
//     recognition.lang = ["en-US"];

//     const onFocusChange = () => {
//       if (document.activeElement !== fieldRef.current) {
//         recognition.stop();
//         setMicActive(false);
//         setMicBlink(false);
//       }
//     };

//     recognition.onresult = (event) => {
//       const transcript = Array.from(event.results)
//         .map((result) => result[0])
//         .map((result) => result.transcript)
//         .join("");

//       if (document.activeElement) {
//         const activeElementId = document.activeElement.id;
//         if (activeElementId === "firstName") {
//           setFirstName(transcript);
//         } else if (activeElementId === "lastName") {
//           setLastName(transcript);
//         }
//       }
//     };

//     recognition.onend = () => {
//       setMicBlink(false);
//     };

//     fieldRef.current.addEventListener("blur", onFocusChange);

//     recognition.start();
//   };

//   // Function to handle changes in first name input
//   const handleFirstNameChange = (e) => {
//     setFirstName(e.target.value);
//     setFirstNameError(false);
//   };
//   const handledobChange = (e) => {
//     setDob(e.target.value);
//     setDobError(false);
//   };

//   // Function to handle changes in last name input
//   const handleLastNameChange = (e) => {
//     setLastName(e.target.value);
//     setLastNameError(false);
//   };

//   // Function to handle submission of user's name
//   const handleNameofUser = () => {
//     if (firstName.trim() === "") {
//       setFirstNameError(true);
//     }

//     if (lastName.trim() === "") {
//       setLastNameError(true);
//     }
//     if (dob.trim() === "") {
//       setLastNameError(true);
//     }

//     // Update form data and step data
//     setFormData((prevFormData) => ({
//       ...prevFormData,
//       firstName: firstName.trim(),
//       lastName: lastName.trim(),
//       dob: dob.trim(),
//     }));

//     updateStep1Data({
//       firstName: firstName.trim(),
//       lastName: lastName.trim(),
//       dob: dob.trim(),
//       selectedImage: selectedImage,
//     });

//     // Proceed to next step if first name and last name are not empty
//     // if (firstName.trim() !== "" && lastName.trim() !== "" && dob.trim() !== "") {
//     //   setActiveStep((prevActiveStep) => prevActiveStep + 1);
//     // }
//     if (firstName.trim() !== "" && lastName.trim() !== "" && dob.trim() !== "") {
//     // Instead of setActiveStep, scroll to next section
//     if (typeof setActiveStep === 'function') {
//       setActiveStep(); // This will scroll to next section
//     }
//   }
//   };

//   // Effect to initialize cropper once selected image changes
//   useEffect(() => {
//     if (openDialog && selectedImage && !cropperInitialized && cropperRef.current) {
//       console.log("Initializing cropper...");
//       setCropperInitialized(true);
//     }
//   }, [openDialog, selectedImage, cropperInitialized]);

//   // Function to handle cropper initialization
//   const handleCropperInitialized = (instance) => {
//     cropperRef.current = instance;
//   };


//   return (
//     <div>
//       <h2 className="onboard-slide-title">Hi, Welcome to Neoin!</h2>
//       <p className="paragraph-text">
//         Hey there! Want to make your profile stand out? Adding a profile picture
//         and your full name is a great way to do it!
//       </p>
//       <div className={profilePicUrl ? "avatar-container" : "avatar-placeholder"}>
//         {profilePicUrl ? (
//           <img src={profilePicUrl} alt="Uploaded" className="uploaded-image" />
//         ) : (
//           <img src={frame} alt="Upload" className="" onClick={() => document.getElementById("fileInput").click()} />
//         )}
//       </div>

//       <input
//         type="file"
//         id="fileInput"
//         accept="image/*"
//         style={{ display: "none" }}
//         onChange={handleFileSelect}
//       />

//       <div className="upload-button-container my-3">
//         <Button
//           variant="outlined"
//           className="next-btn upload-button primary-outlined"
//           onClick={() => document.getElementById("fileInput").click()}
//         >
//           Browse
//         </Button>
//       </div>

//       <div className='login-fields-container'>
//         <div className="login-fields">
//           <div className="a-inactive-enabled">
//             <div className="overlap-group-wrapper">
//               <div className="overlap-group-2">
//                 <div
//                   className={`text-field-container mb-3 ${firstNameError ? "error" : ""}`}
//                 >
//                   <TextField
//                     inputRef={firstNameRef}
//                     className="text-field-outline input-text mb-2"
//                     id="firstName"
//                     name="firstName"
//                     label="First Name"
//                     variant="outlined"
//                     required
//                     error={firstNameError}
//                     value={firstName}
//                     onChange={handleFirstNameChange}
//                     enabled={micActive}
//                   />
//                 </div>
//               </div>
//             </div>
//           </div>

//           <div className="a-inactive-enabled-wrapper">
//             <div className="overlap-group-wrapper">
//               <div className="overlap-group-2">
//                 <div
//                   className={`text-field-container ${lastNameError ? "error" : ""}`}
//                 >
//                   <TextField
//                     inputRef={lastNameRef}
//                     className="text-field-outline input-text mb-2"
//                     id="lastName"
//                     name="lastName"
//                     label="Last Name"
//                     variant="outlined"
//                     required
//                     error={lastNameError}
//                     value={lastName}
//                     onChange={handleLastNameChange}
//                     enabled={micActive}
//                   />
//                 </div>
//               </div>
//             </div>
//           </div>

//           <div className="a-inactive-enabled-wrapper">
//             <div className="overlap-group-wrapper">
//               <div className="overlap-group-2">
//                 <div
//                   className={`text-field-container ${dobError ? "error" : ""}`}
//                 >
//                   {/* <TextField
//                     inputRef={dobRef}
//                     className="text-field-outline input-text mb-2"
//                     id="date of birth"
//                     name="dob"
//                     label="Date of Birth"
//                     variant="outlined"
//                     required
//                     error={dobError}
//                     value={dob}
//                     onChange={handledobChange}
//                     enabled={micActive}
//                   /> */}

//                   <TextField
//                     inputRef={dobRef}
//                     label="Date of Birth"
//                     type="date"
//                     className="text-field-outline input-text mb-2"
//                     id="date of birth"
//                     name="dob"
//                     required
//                     error={dobError}
//                     value={dob}
//                     onChange={handledobChange}
//                     enabled={micActive}
//                     variant="outlined"

//                     InputLabelProps={{ shrink: true }}
//                   // inputProps={{ max: today }}
//                   // fullWidth
//                   // disabled={!isEdit}
//                   />
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         <div className="next-button-container-1 pb-3">
//           <Button
//             variant="outlined"
//             className={`next-btn primary-outlined`}
//             onClick={handleNameofUser}
//             disabled={firstName.trim() === "" || lastName.trim() === "" || dob.trim() === ""}
//           >
//             Next
//           </Button>
//         </div>
//       </div>

//       <Dialog open={openDialog} onClose={handleCropClose}>
//         <DialogTitle>Crop Image</DialogTitle>
//         <DialogContent>
//           <div style={{ width: '100%', height: '400px', position: 'relative' }}>
//             <Cropper
//               ref={cropperRef}
//               src={selectedImage}
//               style={{ height: '100%', width: '100%' }}
//               initialAspectRatio={1}
//               guides={true}
//               cropBoxMovable={true}
//               cropBoxResizable={true}
//               crossOrigin="anonymous"
//               dragMode="crop"
//               viewMode={1}
//               background={false}
//               responsive={true}
//               autoCrop={false}
//               autoCropArea={1}
//               minCropBoxHeight={20}
//               minCropBoxWidth={20}
//               checkOrientation={false}
//               onInitialized={handleCropperInitialized}
//             />
//           </div>
//         </DialogContent>
//         <DialogContent>
//           <div className="d-flex justify-content-end mt-3 gap-3">
//             <Button onClick={handleCropClose} className="hover-red-bg">Cancel</Button>
//             <Button onClick={handleCropSave} variant="contained" sx={{ backgroundColor: '#0044C6' }}>Save</Button>
//           </div>
//         </DialogContent>
//       </Dialog>
//     </div>
//   );
// };

// export default ActiveStep1;